import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAccount } from '../../api/auth';
import { User, Cpu, Shield, Bell, Clock, FileText, Heart, HelpCircle, ExternalLink, LogOut, Trash2, ChevronRight, Pencil, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getMyDevices } from '../../api/devices';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { EditProfile } from './EditProfile';
import { DeviceManagement } from './DeviceManagement';
type Section = 'personal' | 'devices' | 'security' | 'notifications' | 'about' | 'help';
export function ProfileScreen() {
  const navigate = useNavigate();
  const {
    user,
    token,
    logout
  } = useAuth();
  const [deviceCount, setDeviceCount] = useState<number | null>(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  // Desktop master-detail state
  const [activeSection, setActiveSection] = useState<Section>('personal');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  // Bottom sheet states
  const [showSecuritySheet, setShowSecuritySheet] = useState(false);
  const [showAboutSheet, setShowAboutSheet] = useState(false);
  const [showHelpSheet, setShowHelpSheet] = useState(false);
  const [showLogoutSheet, setShowLogoutSheet] = useState(false);
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  // Delete account state
  const [deleteInput, setDeleteInput] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (token) {
      setIsLoadingDevices(true);
      getMyDevices(token).then(devices => setDeviceCount(devices.length)).catch(err => {}).finally(() => setIsLoadingDevices(false));
    }
  }, [token]);
  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  const getMemberSince = (dateString: string) => {
    if (!dateString) return 'Member since recently';
    const date = new Date(dateString);
    return `Member since ${date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })}`;
  };
  const handleNavigation = (path: string, section: Section) => {
    if (isDesktop) {
      setActiveSection(section);
    } else {
      navigate(path);
    }
  };
  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      logout();
      navigate('/splash');
    }, 800);
  };
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleDeleteAccount = async () => {
    if (!token) return;
    setIsLoggingOut(true);
    setDeleteError(null);
    try {
      await deleteAccount(token);
      logout();
      navigate('/intro');
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
      setIsLoggingOut(false);
    }
  };
  const renderMenuContent = () => <div className="space-y-6 pb-20 md:pb-0" data-id="element-1963" data-mp="8pnws3svktx6">
      {/* User Card */}
      <div className="bg-white rounded-[24px] p-6 shadow-card relative" data-id="element-1964" data-mp="3rchhdjt9r7k">
        <button onClick={() => handleNavigation('/app/profile/edit', 'personal')} className="absolute top-6 right-6 w-[36px] h-[36px] rounded-full bg-[#E9D5FF] flex items-center justify-center hover:bg-[#E5E2DD] transition-colors" aria-label="Edit Profile" data-id="element-1965" data-mp="3p9lmcvz93ux">
          <Pencil size={16} className="text-text-secondary" data-id="element-1966" data-mp="3l5rgxonj0jpx" />
        </button>

        <div className="flex flex-col items-center text-center" data-id="element-1967" data-mp="3h5r242wim6ic">
          <div className="w-[80px] h-[80px] rounded-full bg-primary flex items-center justify-center text-white text-[28px] font-bold mb-4" data-id="element-1968" data-mp="3tw00497tzyzp">
            {getInitials(user?.fullName)}
          </div>
          <h2 className="text-[22px] font-semibold text-text-primary mb-1" data-id="element-1969" data-mp="7nrvmut301ev">
            {user?.fullName || 'User'}
          </h2>
          <p className="text-[15px] text-text-secondary mb-3" data-id="element-1970" data-mp="cgkwv8pccrm7">
            {user?.email || 'user@example.com'}
          </p>
          <div className="bg-primary/20 text-primary text-[13px] font-medium px-3 py-1 rounded-pill mb-2" data-id="element-1971" data-mp="hg5vfi0lmxye">
            Active Account
          </div>
          <p className="text-[13px] text-text-secondary" data-id="element-1972" data-mp="qehw6cv6jc4k">
            {getMemberSince(user?.createdAt)}
          </p>
        </div>
      </div>

      {/* Account Section */}
      <div data-id="element-1973" data-mp="2s6e5dftvk9w6">
        <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-3 px-2" data-id="element-1974" data-mp="2s6nw55mqul2x">
          Account
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-1975" data-mp="2x5r2quidgzd0">
          <MenuRow icon={<User size={20} className="text-primary" data-id="element-1977" data-mp="2pn10ro1w46nq" />} iconBg="bg-primary/15" title="Personal Information" subtitle="Name, email, phone" onClick={() => handleNavigation('/app/profile/edit', 'personal')} isActive={isDesktop && activeSection === 'personal'} data-id="element-1976" data-mp="2pmrkis6nddv8" />
          <div className="h-[1px] w-full bg-border" data-id="element-1978" data-mp="2auz8ap25gfxq" />
          <MenuRow icon={<Cpu size={20} className="text-primary" data-id="element-1980" data-mp="38ihoum21ymca" />} iconBg="bg-primary/15" title="Connected Devices" subtitle={isLoadingDevices ? <SkeletonBlock width="100px" height="16px" className="mt-1" data-id="element-1981" data-mp="2ykjqh5qhb95y" /> : deviceCount === 0 ? 'No devices' : `${deviceCount} device${deviceCount === 1 ? '' : 's'} connected`} onClick={() => handleNavigation('/app/profile/devices', 'devices')} isActive={isDesktop && activeSection === 'devices'} data-id="element-1979" data-mp="38j9b369rf7pw" />
          <div className="h-[1px] w-full bg-border" data-id="element-1982" data-mp="eaapdttghsyn" />
          <MenuRow icon={<Shield size={20} className="text-success" data-id="element-1984" data-mp="e86n1gb7h20h" />} iconBg="bg-success/15" title="Security" subtitle="Password and account security" onClick={() => {
          if (isDesktop) setActiveSection('security');else setShowSecuritySheet(true);
        }} isActive={isDesktop && activeSection === 'security'} data-id="element-1983" data-mp="e8zpi534t5ct" />
        </div>
      </div>

      {/* Notifications Section */}
      <div data-id="element-1985" data-mp="3dqh2xifetqkj">
        <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-3 px-2" data-id="element-1986" data-mp="3dqpkhnmh9k22">
          Notifications
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-1987" data-mp="33r8d91rkuy3z">
          <MenuRow icon={<Bell size={20} className="text-primary" data-id="element-1989" data-mp="38q1sozqfcz6s" />} iconBg="bg-primary/15" title="Alert Notifications" subtitle="Get notified when alerts are triggered" rightElement={<ToggleSwitch storageKey="aurora_alert_notifications" defaultChecked={true} label="Alert Notifications" data-id="element-1990" data-mp="2l3pskeb9pk8e" />} data-id="element-1988" data-mp="33tbdeemg27mf" />
          <div className="h-[1px] w-full bg-border" data-id="element-1991" data-mp="2u3crmtgiydya" />
          <MenuRow icon={<Clock size={20} className="text-[#F59E0B]" data-id="element-1993" data-mp="382vq0ujjaaxe" />} iconBg="bg-[#F59E0B]/15" title="Session Reminders" subtitle="Daily reminder to monitor" rightElement={<ToggleSwitch storageKey="aurora_session_reminders" defaultChecked={false} label="Session Reminders" data-id="element-1994" data-mp="3htgngenh19eq" />} data-id="element-1992" data-mp="2u5z0cwf5izi3" />
          <div className="h-[1px] w-full bg-border" data-id="element-1995" data-mp="3rqldipkftp0t" />
          <MenuRow icon={<FileText size={20} className="text-success" data-id="element-1997" data-mp="3msjozp48sxhy" />} iconBg="bg-success/15" title="Report Notifications" subtitle="Notify when report is delivered" rightElement={<ToggleSwitch storageKey="aurora_report_notifications" defaultChecked={true} label="Report Notifications" data-id="element-1998" data-mp="2ce0t3851zqvv" />} data-id="element-1996" data-mp="3rp0f2ov05lxo" />
        </div>
      </div>

      {/* App Section */}
      <div data-id="element-1999" data-mp="2mavnpfy7h74v">
        <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-3 px-2" data-id="element-2000" data-mp="1oppxaje7sheg">
          App
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-2001" data-mp="1tpm22kuqkcv2">
          <MenuRow icon={<Heart size={20} className="text-primary" data-id="element-2003" data-mp="1ymvqbx6e2ihb" />} iconBg="bg-primary/15" title="About Aurora Care" subtitle="Version 1.0.0" onClick={() => {
          if (isDesktop) setActiveSection('about');else setShowAboutSheet(true);
        }} isActive={isDesktop && activeSection === 'about'} data-id="element-2002" data-mp="1yn421lqrzhqx" />
          <div className="h-[1px] w-full bg-border" data-id="element-2004" data-mp="3o8mcnm45wb53" />
          <MenuRow icon={<HelpCircle size={20} className="text-success" data-id="element-2006" data-mp="3k70tjj11b0mg" />} iconBg="bg-success/15" title="Help & Support" subtitle="FAQs and contact support" onClick={() => {
          if (isDesktop) setActiveSection('help');else setShowHelpSheet(true);
        }} isActive={isDesktop && activeSection === 'help'} data-id="element-2005" data-mp="3o605hnl6nxne" />
          <div className="h-[1px] w-full bg-border" data-id="element-2007" data-mp="30j0e90vm5zy5" />
          <MenuRow icon={<FileText size={20} className="text-success" data-id="element-2009" data-mp="30gxbrhd9yecj" />} iconBg="bg-success/15" title="Privacy Policy" rightElement={<ExternalLink size={18} className="text-text-secondary" data-id="element-2010" data-mp="2rjd8y3zrk9zt" />} onClick={() => window.open('https://example.com/privacy', '_blank')} data-id="element-2008" data-mp="30h77lll03fq5" />
        </div>
      </div>

      {/* Danger Zone */}
      <div data-id="element-2011" data-mp="2hlz5r62uphpg">
        <h3 className="text-[12px] uppercase font-semibold text-danger tracking-[1px] mb-3 px-2" data-id="element-2012" data-mp="2hm8ny7hnmkkl">
          Account Actions
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-2013" data-mp="7da2kmuxns6j">
          <MenuRow icon={<LogOut size={20} className="text-danger" data-id="element-2015" data-mp="l2u0nfdkk19i" />} iconBg="bg-danger/15" title={<span className="text-danger" data-id="element-2016" data-mp="h3cuejqbj03a">Sign Out</span>} onClick={() => setShowLogoutSheet(true)} hideChevron data-id="element-2014" data-mp="l3na4o3wx4vc" />
          <div className="h-[1px] w-full bg-border" data-id="element-2017" data-mp="3pk7p3bwype6a" />
          <MenuRow icon={<Trash2 size={20} className="text-danger" data-id="element-2019" data-mp="3kkldf6ws85zl" />} iconBg="bg-danger/15" title={<span className="text-danger" data-id="element-2020" data-mp="2eyr67ozp9m7">Delete Account</span>} subtitle={<span className="text-danger/70" data-id="element-2021" data-mp="3tix6dfl4ffv7">
                Permanently delete your account and data
              </span>} onClick={() => setShowDeleteSheet(true)} hideChevron data-id="element-2018" data-mp="3kk242el76px6" />
        </div>
      </div>
    </div>;
  const renderRightPanel = () => {
    switch (activeSection) {
      case 'personal':
        return <EditProfile isPanel data-id="element-2022" data-mp="1lclbk575ht9v" />;
      case 'devices':
        return <DeviceManagement isPanel data-id="element-2023" data-mp="1l9z4e6o69fs6" />;
      case 'security':
        return <div className="bg-white rounded-[24px] p-8 shadow-card h-full flex flex-col items-center justify-center text-center" data-id="element-2024" data-mp="17dvegpkbztli">
            <Shield size={48} className="text-success mb-4" data-id="element-2025" data-mp="1cd8d07kkep3x" />
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-2026" data-mp="202apz2497ts3">
              Security Settings
            </h2>
            <p className="text-[15px] text-text-secondary max-w-[300px]" data-id="element-2027" data-mp="2525hn7vusne7">
              Password change via email link — coming soon.
            </p>
          </div>;
      case 'about':
        return <div className="bg-white rounded-[24px] p-8 shadow-card h-full flex flex-col items-center justify-center text-center" data-id="element-2028" data-mp="1v7wtsxhrpimd">
            <div className="w-[80px] h-[80px] bg-primary rounded-full flex items-center justify-center mb-4" data-id="element-2029" data-mp="2nr2ftmudg01p">
              <Heart size={36} className="text-white fill-white" data-id="element-2030" data-mp="2xmspnyhqw2s6" />
            </div>
            <h2 className="text-[28px] font-semibold text-text-primary mb-1" data-id="element-2031" data-mp="2smy327505zcx">
              Aurora Care
            </h2>
            <p className="text-[15px] text-text-secondary mb-8" data-id="element-2032" data-mp="2nnbptxp6cj44">
              Version 1.0.0
            </p>

            <div className="w-full max-w-[300px] h-[1px] bg-border mb-6" data-id="element-2033" data-mp="2jlzwbh36jdqn" />

            <p className="text-[14px] text-text-secondary mb-3" data-id="element-2034" data-mp="2enycuv1udcen">Built with</p>
            <div className="flex gap-2 mb-8" data-id="element-2035" data-mp="29x89fncz7jml">
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2036" data-mp="29vdm62e8fffc">
                React
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2037" data-mp="1zz293a6tilju">
                TypeScript
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2038" data-mp="1vwdwcrezguhl">
                WebSockets
              </span>
            </div>

            <div className="w-full max-w-[300px] h-[1px] bg-border mb-6" data-id="element-2039" data-mp="6f9yx46dn9eo" />

            <p className="text-[13px] text-text-secondary" data-id="element-2040" data-mp="bed6owdqrtqc">
              © 2026 Aurora Care
            </p>
          </div>;
      case 'help':
        return <div className="bg-white rounded-[24px] p-8 shadow-card h-full flex flex-col items-center justify-center text-center" data-id="element-2041" data-mp="kfuja4rxxdaa">
            <HelpCircle size={48} className="text-success mb-4" data-id="element-2042" data-mp="3jvr6u5sun3qh" />
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-2043" data-mp="3jt520dxcf2by">
              Help & Support
            </h2>
            <p className="text-[15px] text-text-secondary max-w-[300px] mb-6" data-id="element-2044" data-mp="3sv4fwxd29qao">
              Our support team is here to help you with any questions or issues.
            </p>
            <button className="bg-primary text-white px-6 py-3 rounded-[14px] font-medium" data-id="element-2045" data-mp="306ywcdxqu7cf">
              Contact Support
            </button>
          </div>;
      default:
        return null;
    }
  };
  return <main className="w-full max-w-[1200px] mx-auto animate-fadeInUp" data-id="element-2046" data-mp="1bmtdtmdvodzl">
      {isDesktop ? <div className="flex gap-8 h-[calc(100vh-120px)]" data-id="element-2047" data-mp="16oi55ezbk9iy">
          <div className="w-[340px] flex-shrink-0 overflow-y-auto pr-2 custom-scrollbar" data-id="element-2048" data-mp="16q2y505zjkep">
            {renderMenuContent()}
          </div>
          <div className="flex-1 overflow-y-auto pb-8" data-id="element-2049" data-mp="1fp6hm592e1tq">
            {renderRightPanel()}
          </div>
        </div> : <div className="max-w-[600px] mx-auto" data-id="element-2050" data-mp="1ucrhlqan7kxf">{renderMenuContent()}</div>}

      {/* Security Bottom Sheet (Mobile) */}
      <BottomSheet isOpen={showSecuritySheet} onClose={() => setShowSecuritySheet(false)} data-id="element-2051" data-mp="1pehr3szv58i5">
        <div className="text-center py-4" data-id="element-2052" data-mp="23c6asxi729lm">
          <Shield size={40} className="text-success mx-auto mb-4" data-id="element-2053" data-mp="1zcnpw1qdhyis" />
          <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-2054" data-mp="90htwpsejwyl">
            Security
          </h2>
          <p className="text-[15px] text-text-secondary mb-6" data-id="element-2055" data-mp="4z616be8evm6">
            Password change via email link — coming soon.
          </p>
          <button onClick={() => setShowSecuritySheet(false)} className="w-full py-3 text-text-secondary font-medium" data-id="element-2056" data-mp="3r1mzw315ps8w">
            Close
          </button>
        </div>
      </BottomSheet>

      {/* About Bottom Sheet (Mobile) */}
      <BottomSheet isOpen={showAboutSheet} onClose={() => setShowAboutSheet(false)} data-id="element-2057" data-mp="3m93nvai4b567">
        <div className="text-center py-4" data-id="element-2058" data-mp="37c36o0xjyslz">
          <div className="w-[60px] h-[60px] bg-primary rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-2059" data-mp="37b1jiy39ifnw">
            <Heart size={28} className="text-white fill-white" data-id="element-2060" data-mp="3c933dl8iij0f" />
          </div>
          <h2 className="text-[24px] font-semibold text-text-primary mb-1" data-id="element-2061" data-mp="2yditffarhb55">
            Aurora Care
          </h2>
          <p className="text-[14px] text-text-secondary mb-6" data-id="element-2062" data-mp="33cm0146e3pf8">Version 1.0.0</p>

          <div className="w-full h-[1px] bg-border mb-6" data-id="element-2063" data-mp="wl2zyoyy1cwc" />

          <div className="flex flex-col items-center mb-6" data-id="element-2064" data-mp="11jo42zhbggq2">
            <p className="text-[14px] text-text-secondary mb-3" data-id="element-2065" data-mp="11h1uyves1t5q">Built with</p>
            <div className="flex gap-2" data-id="element-2066" data-mp="z62gwfd40p3h">
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2067" data-mp="v3e7o6khhg5z">
                React
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2068" data-mp="q4bblbmheka5">
                TypeScript
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2069" data-mp="1it026gpwv77n">
                WebSockets
              </span>
            </div>
          </div>

          <div className="w-full h-[1px] bg-border mb-6" data-id="element-2070" data-mp="192dquyp892ur" />

          <p className="text-[13px] text-text-secondary mb-6" data-id="element-2071" data-mp="190uaz97hgtti">
            © 2026 Aurora Care
          </p>

          <button onClick={() => setShowAboutSheet(false)} className="w-full py-3 text-text-secondary font-medium" data-id="element-2072" data-mp="141zsy9hcb9ag">
            Close
          </button>
        </div>
      </BottomSheet>

      {/* Help Bottom Sheet (Mobile) */}
      <BottomSheet isOpen={showHelpSheet} onClose={() => setShowHelpSheet(false)} data-id="element-2073" data-mp="2457cscxjjs86">
        <div className="text-center py-4" data-id="element-2074" data-mp="29394fm918zv9">
          <HelpCircle size={40} className="text-success mx-auto mb-4" data-id="element-2075" data-mp="2e23oeriqkugy" />
          <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-2076" data-mp="2e3wxedgtnqs4">
            Help & Support
          </h2>
          <p className="text-[15px] text-text-secondary mb-6" data-id="element-2077" data-mp="2n2ia3rzm4bkz">
            Our support team is here to help you with any questions or issues.
          </p>
          <button className="w-full h-[52px] bg-primary text-white rounded-[14px] font-medium mb-3" data-id="element-2078" data-mp="2s00m5r8xf6x7">
            Contact Support
          </button>
          <button onClick={() => setShowHelpSheet(false)} className="w-full py-3 text-text-secondary font-medium" data-id="element-2079" data-mp="31r51wt6fevt3">
            Close
          </button>
        </div>
      </BottomSheet>

      {/* Logout Bottom Sheet */}
      <BottomSheet isOpen={showLogoutSheet} onClose={() => !isLoggingOut && setShowLogoutSheet(false)} data-id="element-2080" data-mp="3asmc5ux5k39v">
        <div className="text-center py-2" data-id="element-2081" data-mp="3w4w3g484epn1">
          <div className="w-[48px] h-[48px] bg-primary rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2082" data-mp="3s0eii1q0y6ao">
            <Heart size={24} className="text-white fill-white" data-id="element-2083" data-mp="9ui87kgqzcu8" />
          </div>
          <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-2084" data-mp="4wgpj0n8hfja">
            Sign out of Aurora Care?
          </h2>
          <p className="text-[15px] text-text-secondary mb-8" data-id="element-2085" data-mp="3dd3b8c0vl8gd">
            You will be signed out of your account on this device.
          </p>

          <button onClick={handleLogout} disabled={isLoggingOut} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-opacity disabled:opacity-70 mb-3" data-id="element-2086" data-mp="3n23a7vfdo3uk">
            {isLoggingOut ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2087" data-mp="2pnhavl8ron6s" /> : 'Sign Out'}
          </button>
          <button onClick={() => setShowLogoutSheet(false)} disabled={isLoggingOut} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-2088" data-mp="19vn7vkih8wv5">
            Stay Signed In
          </button>
        </div>
      </BottomSheet>

      {/* Delete Account Bottom Sheet */}
      <BottomSheet isOpen={showDeleteSheet} onClose={() => !isLoggingOut && setShowDeleteSheet(false)} data-id="element-2089" data-mp="1nvxv8dkkvtbk">
        <div className="text-center py-2" data-id="element-2090" data-mp="1ss568krtzv0d">
          <AlertTriangle size={40} className="text-danger mx-auto mb-4" data-id="element-2091" data-mp="1evjcnlz00sbr" />
          <h2 className="text-[18px] font-semibold text-danger mb-2" data-id="element-2092" data-mp="1jtkrfupe65hf">
            Delete your account?
          </h2>
          <p className="text-[15px] text-text-secondary mb-6" data-id="element-2093" data-mp="3fberxuii56f">
            This will permanently delete your account, all your monitoring
            sessions, and all your data. This cannot be undone.
          </p>

          {deleteError && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-4 text-[14px] text-danger font-medium text-center animate-slideDown" data-id="element-2094" data-mp="ilywg44ph536">
              {deleteError}
            </div>}

          <div className="text-left mb-8" data-id="element-2095" data-mp="8s88r1gekyoj">
            <label htmlFor="deleteConfirm" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-2096" data-mp="dnxdrkb6z7s8">
              Type DELETE to confirm
            </label>
            <input id="deleteConfirm" type="text" value={deleteInput} onChange={e => setDeleteInput(e.target.value)} placeholder="Type DELETE here" className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-danger focus:outline-none transition-colors" data-id="element-2097" data-mp="4pbvzrdjnwsi" />
          </div>

          <button onClick={handleDeleteAccount} disabled={deleteInput !== 'DELETE' || isLoggingOut} className={`w-full h-[52px] rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-all mb-3 ${deleteInput === 'DELETE' ? 'bg-danger text-white' : 'bg-border text-text-secondary opacity-50 cursor-not-allowed'}`} data-id="element-2098" data-mp="3h4makekz334p">
            {isLoggingOut ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2099" data-mp="32hf86x9jzdhf" /> : 'Delete Account'}
          </button>
          <button onClick={() => {
          setShowDeleteSheet(false);
          setDeleteInput('');
        }} disabled={isLoggingOut} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-2100" data-mp="3cg3b0osyvm0j">
            Cancel
          </button>
        </div>
      </BottomSheet>
    </main>;
}
// Helper Components
function MenuRow({
  icon,
  iconBg,
  title,
  subtitle,
  rightElement,
  onClick,
  hideChevron = false,
  isActive = false
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  rightElement?: React.ReactNode;
  onClick?: () => void;
  hideChevron?: boolean;
  isActive?: boolean;
}) {
  return <div onClick={onClick} className={`p-4 flex items-center gap-4 transition-colors ${onClick ? 'cursor-pointer hover:bg-secondary/50' : ''} ${isActive ? 'bg-primary/5 border-l-4 border-primary' : 'border-l-4 border-transparent'}`} data-id="element-2101" data-mp="31t3bvwp4apz8">
      <div className={`w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`} data-id="element-2102" data-mp="3gihiwy580vbg">
        {icon}
      </div>
      <div className="flex-1 min-w-0" data-id="element-2103" data-mp="3pkiii1nxftga">
        <div className="text-[15px] font-medium text-text-primary" data-id="element-2104" data-mp="3pj7cdv70e9m0">{title}</div>
        {subtitle && <div className="text-[13px] text-text-secondary mt-0.5" data-id="element-2105" data-mp="1q8ob4h3lq0cr">
            {subtitle}
          </div>}
      </div>
      {rightElement ? rightElement : !hideChevron && onClick ? <ChevronRight size={18} className="text-text-secondary flex-shrink-0" data-id="element-2106" data-mp="1nojetpl54jjb" /> : null}
    </div>;
}
function BottomSheet({
  isOpen,
  onClose,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-2107" data-mp="18ykg9lpob63b">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-2108" data-mp="18vzbooxzqc1v" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-2109" data-mp="1wphho4juel0b">
            <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6 md:hidden" data-id="element-2110" data-mp="1v6fvbh1aj202" />
            {children}
          </motion.div>
        </>}
    </AnimatePresence>;
}